/**
 * File Picker Dialog
 * Browse and select files from the file library
 */

import { useState, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
    Folder,
    File as FileIcon,
    Image,
    Film,
    Music,
    FileText,
    Archive,
    Search,
    ChevronRight,
    Home,
    Check,
} from 'lucide-react';
import { api, type ApiListResponse } from '@/lib/api';
import { cn } from '@/lib/utils';

interface FileItem {
    id: string;
    filename_download: string;
    title: string | null;
    type: string | null;
    filesize: number | null;
    folder: string | null;
    modified_on: string;
}

interface FolderItem {
    id: string;
    name: string;
    parent: string | null;
}

interface FilePickerDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onSelect: (fileId: string, file: FileItem) => void;
    selectedFileId?: string | null;
    folder?: string;
    multiple?: boolean;
    accept?: string;
}

// Get file icon based on MIME type
function getFileIcon(type: string | null) {
    if (!type) return <FileIcon className="h-5 w-5" />;
    if (type.startsWith('image/')) return <Image className="h-5 w-5 text-blue-600" />;
    if (type.startsWith('video/')) return <Film className="h-5 w-5 text-purple-600" />;
    if (type.startsWith('audio/')) return <Music className="h-5 w-5 text-green-600" />;
    if (type.includes('pdf') || type.includes('document') || type.includes('text'))
        return <FileText className="h-5 w-5 text-red-600" />;
    if (type.includes('zip') || type.includes('tar') || type.includes('compress'))
        return <Archive className="h-5 w-5 text-orange-600" />;
    return <FileIcon className="h-5 w-5" />;
}

// Format file size
function formatFileSize(bytes: number | null): string {
    if (bytes === null || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

// Get file thumbnail URL
function getFileThumbnail(file: FileItem): string | null {
    if (!file.type?.startsWith('image/')) return null;
    const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8055';
    return `${API_URL}/assets/${file.id}?width=80&height=80&fit=cover&key=system-small-cover`;
}

export function FilePickerDialog({
    open,
    onOpenChange,
    onSelect,
    selectedFileId,
    folder: initialFolder,
    multiple = false,
    accept,
}: FilePickerDialogProps) {
    const [files, setFiles] = useState<FileItem[]>([]);
    const [folders, setFolders] = useState<FolderItem[]>([]);
    const [currentFolder, setCurrentFolder] = useState<string | null>(initialFolder || null);
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [breadcrumbs, setBreadcrumbs] = useState<FolderItem[]>([]);

    // Build breadcrumb trail
    useEffect(() => {
        const buildBreadcrumbs = async () => {
            if (!currentFolder) {
                setBreadcrumbs([]);
                return;
            }

            try {
                const trail: FolderItem[] = [];
                let folderId: string | null = currentFolder;

                while (folderId) {
                    const response: { data: FolderItem } = await api.get(`/folders/${folderId}`);
                    const folder: FolderItem = response.data;
                    trail.unshift(folder);
                    folderId = folder.parent;
                }

                setBreadcrumbs(trail);
            } catch (error) {
                console.error('Error building breadcrumbs:', error);
            }
        };

        buildBreadcrumbs();
    }, [currentFolder]);

    // Initialize selected IDs when dialog opens
    useEffect(() => {
        if (open && selectedFileId) {
            setSelectedIds(new Set([selectedFileId]));
        } else if (open && !selectedFileId) {
            setSelectedIds(new Set());
        }
    }, [open, selectedFileId]);

    // Fetch files and folders
    useEffect(() => {
        if (!open) return;

        const fetchData = async () => {
            setLoading(true);
            try {
                // Build filter for current location
                const filterArray: Record<string, unknown>[] = [
                    { type: { _nnull: true } }, // Exclude folders
                ];

                if (currentFolder) {
                    filterArray.push({ folder: { _eq: currentFolder } });
                } else {
                    filterArray.push({ folder: { _null: true } });
                }

                // Add search filter
                if (searchQuery) {
                    filterArray.push({
                        _or: [
                            { filename_download: { _contains: searchQuery } },
                            { title: { _contains: searchQuery } },
                        ],
                    });
                }

                // Build query parameters - use array format for fields and sort
                const params = new URLSearchParams();
                params.append('limit', '100');
                params.append('sort[]', '-modified_on');

                // Add fields as array parameters
                ['id', 'filename_download', 'title', 'type', 'filesize', 'folder', 'modified_on'].forEach(field => {
                    params.append('fields[]', field);
                });

                // Add filter as nested query parameters
                filterArray.forEach((filterItem, index) => {
                    Object.entries(filterItem).forEach(([key, value]) => {
                        if (key === '_or') {
                            // Handle OR conditions
                            (value as Array<any>).forEach((orItem, orIndex) => {
                                Object.entries(orItem).forEach(([orKey, orValue]: [string, any]) => {
                                    Object.entries(orValue).forEach(([operator, opValue]) => {
                                        params.append(`filter[_and][${index}][${key}][${orIndex}][${orKey}][${operator}]`, String(opValue));
                                    });
                                });
                            });
                        } else {
                            // Handle regular conditions
                            Object.entries(value as Record<string, any>).forEach(([operator, opValue]) => {
                                params.append(`filter[_and][${index}][${key}][${operator}]`, opValue === true ? 'true' : opValue === null ? 'true' : String(opValue));
                            });
                        }
                    });
                });

                const filesResponse = await api.get<ApiListResponse<FileItem>>(`/files?${params.toString()}`);

                const allFiles = filesResponse.data || [];
                const filtered = accept && accept.includes('image/*')
                    ? allFiles.filter((file) => file.type?.startsWith('image/'))
                    : allFiles;

                setFiles(filtered);

                // Fetch folders (only if not searching)
                if (!searchQuery) {
                    const folderParams = new URLSearchParams();
                    folderParams.append('sort[]', 'name');

                    if (currentFolder) {
                        folderParams.append('filter[parent][_eq]', currentFolder);
                    } else {
                        folderParams.append('filter[parent][_null]', 'true');
                    }

                    const foldersResponse = await api.get<ApiListResponse<FolderItem>>(`/folders?${folderParams.toString()}`);
                    setFolders(foldersResponse.data || []);
                } else {
                    setFolders([]);
                }
            } catch (error) {
                console.error('Error fetching files:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [open, currentFolder, searchQuery, accept]);

    const handleFileClick = (file: FileItem) => {
        // Enforce accept filter when selecting
        if (accept && accept.includes('image/*') && !file.type?.startsWith('image/')) {
            return;
        }

        if (multiple) {
            const newSelectedIds = new Set(selectedIds);
            if (newSelectedIds.has(file.id)) {
                newSelectedIds.delete(file.id);
            } else {
                newSelectedIds.add(file.id);
            }
            setSelectedIds(newSelectedIds);
        } else {
            setSelectedIds(new Set([file.id]));
        }
    };

    const handleConfirm = () => {
        if (selectedIds.size > 0) {
            const firstId = Array.from(selectedIds)[0]!;
            const selectedFile = files.find(f => f.id === firstId);
            if (selectedFile) {
                onSelect(firstId, selectedFile);
                onOpenChange(false);
            }
        }
    };

    const handleFolderClick = (folderId: string) => {
        setCurrentFolder(folderId);
        setSearchQuery('');
    };

    const handleBreadcrumbClick = (folderId: string | null) => {
        setCurrentFolder(folderId);
        setSearchQuery('');
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-4xl max-h-[80vh] flex flex-col">
                <DialogHeader>
                    <DialogTitle>Choose File</DialogTitle>
                    <DialogDescription>
                        Select a file from your library
                    </DialogDescription>
                </DialogHeader>

                {/* Search and Breadcrumbs */}
                <div className="space-y-3">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                            placeholder="Search files..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-9"
                        />
                    </div>

                    {/* Breadcrumbs */}
                    {!searchQuery && (
                        <div className="flex items-center gap-1 text-sm text-muted-foreground">
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleBreadcrumbClick(null)}
                                className="h-7 px-2"
                            >
                                <Home className="h-3.5 w-3.5 mr-1" />
                                Root
                            </Button>
                            {breadcrumbs.map((folder) => (
                                <div key={folder.id} className="flex items-center gap-1">
                                    <ChevronRight className="h-3.5 w-3.5" />
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleBreadcrumbClick(folder.id)}
                                        className="h-7 px-2"
                                    >
                                        {folder.name}
                                    </Button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* File List */}
                <ScrollArea className="flex-1 border rounded-md">
                    {loading ? (
                        <div className="flex items-center justify-center h-64 text-muted-foreground">
                            Loading...
                        </div>
                    ) : (
                        <div className="p-2 space-y-1">
                            {/* Folders */}
                            {folders.map((folder) => (
                                <button
                                    key={folder.id}
                                    onClick={() => handleFolderClick(folder.id)}
                                    className="w-full flex items-center gap-3 p-3 rounded-md hover:bg-muted transition-colors"
                                >
                                    <Folder className="h-5 w-5 text-yellow-600 shrink-0" />
                                    <span className="flex-1 text-left font-medium">{folder.name}</span>
                                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                                </button>
                            ))}

                            {/* Files */}
                            {files.map((file) => {
                                const isSelected = selectedIds.has(file.id);
                                const thumbnail = getFileThumbnail(file);

                                return (
                                    <button
                                        key={file.id}
                                        onClick={() => handleFileClick(file)}
                                        className={cn(
                                            'w-full flex items-center gap-3 p-3 rounded-md hover:bg-muted transition-colors',
                                            isSelected && 'bg-primary/10 ring-2 ring-primary'
                                        )}
                                    >
                                        <div className="shrink-0 w-12 h-12 rounded overflow-hidden bg-muted flex items-center justify-center">
                                            {thumbnail ? (
                                                <img
                                                    src={thumbnail}
                                                    alt={file.title || file.filename_download}
                                                    className="w-full h-full object-cover"
                                                />
                                            ) : (
                                                getFileIcon(file.type)
                                            )}
                                        </div>

                                        <div className="flex-1 text-left min-w-0">
                                            <div className="font-medium truncate">
                                                {file.title || file.filename_download}
                                            </div>
                                            <div className="text-sm text-muted-foreground flex items-center gap-2">
                                                <span>{formatFileSize(file.filesize)}</span>
                                                {file.type && (
                                                    <>
                                                        <span>•</span>
                                                        <span className="truncate">{file.type.split('/')[1]?.toUpperCase()}</span>
                                                    </>
                                                )}
                                            </div>
                                        </div>

                                        {isSelected && (
                                            <Check className="h-5 w-5 text-primary shrink-0" />
                                        )}
                                    </button>
                                );
                            })}

                            {folders.length === 0 && files.length === 0 && (
                                <div className="text-center text-muted-foreground py-12">
                                    {searchQuery ? 'No files found' : 'This folder is empty'}
                                </div>
                            )}
                        </div>
                    )}
                </ScrollArea>

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>
                        Cancel
                    </Button>
                    <Button onClick={handleConfirm} disabled={selectedIds.size === 0}>
                        Select {selectedIds.size > 0 && `(${selectedIds.size})`}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
